import { hashCanonicalContent } from "@cogo/schema/canonical-content-hash"
import {
  applicationNamespace,
  blockNamespace,
  exactResourceId,
  pageNamespace,
  uxInstanceNamespace,
} from "../library-composition/exact-namespace"
import type { SourceInteractionRelationSetV1 } from "./types"

const NEWSLETTER_L4_RELATION_TEMPLATE = deepFreeze({
  schemaVersion: "cogo.source-interaction-relation-set.v1" as const,
  relationSetIdPrefix: "newsletter-membership-",
  relationSetVersion: "1.0.0",
  applicationBlueprintId: "newsletter-membership",
  applicationBlueprintRef: "newsletter-membership@1.0.0",
  uxPatternRefs: [
    "newsletter-member-content@1.0.0",
    "newsletter-membership-signup@1.0.0",
    "newsletter-operator-impact-controls@1.0.0",
    "newsletter-operator-overview@1.0.0",
  ],
  designOntologyReleaseRef: "library-baseline-web-v1",
  designSourceRefs: ["urn:cogo:approved-source:library-baseline-web-v1"],
  evidenceRefs: ["cogo:approved-source/newsletter-membership-signup-v1"],
  relations: [
    {
      uxInstanceId: "membership-signup",
      pageId: "join-membership",
      blockId: "join-membership",
      semanticNodeId: "join-membership",
      componentRef: "library-baseline/primary-button",
      eventType: "tap",
      flowId: "join",
    },
  ],
})

/**
 * Human-authored interaction authority for the canonical generated UI Block
 * journey. The semantic relation and every local identifier are closed here;
 * only the caller-owned application instance segment is projected through the
 * same exact namespace constructors as the compiler. No title, label, prompt,
 * or fuzzy catalog lookup participates in the projection.
 */
export function instantiateCanonicalNewsletterSourceInteractionRelationSet(
  applicationInstanceId: string,
): Readonly<SourceInteractionRelationSetV1> {
  const applicationNamespaceId = applicationNamespace(
    NEWSLETTER_L4_RELATION_TEMPLATE.applicationBlueprintId,
    applicationInstanceId,
  )
  const relations = NEWSLETTER_L4_RELATION_TEMPLATE.relations.map((relation) => {
    const uxNamespaceId = uxInstanceNamespace(
      applicationNamespaceId,
      relation.uxInstanceId,
    )
    const semanticNodeId = exactResourceId(
      uxNamespaceId,
      "node",
      relation.semanticNodeId,
    )
    return {
      blockId: blockNamespace(
        pageNamespace(uxNamespaceId, relation.pageId),
        relation.blockId,
      ),
      sourceNodeId: semanticNodeId,
      elementId: semanticNodeId,
      componentRef: relation.componentRef,
      eventType: relation.eventType,
      flowId: exactResourceId(uxNamespaceId, "flow", relation.flowId),
    }
  })
  const body = {
    schemaVersion: NEWSLETTER_L4_RELATION_TEMPLATE.schemaVersion,
    relationSetId: `${NEWSLETTER_L4_RELATION_TEMPLATE.relationSetIdPrefix}${applicationInstanceId}@${NEWSLETTER_L4_RELATION_TEMPLATE.relationSetVersion}`,
    applicationBlueprintRef:
      NEWSLETTER_L4_RELATION_TEMPLATE.applicationBlueprintRef,
    applicationInstanceId,
    uxPatternRefs: [...NEWSLETTER_L4_RELATION_TEMPLATE.uxPatternRefs],
    designOntologyReleaseRef:
      NEWSLETTER_L4_RELATION_TEMPLATE.designOntologyReleaseRef,
    designSourceRefs: [...NEWSLETTER_L4_RELATION_TEMPLATE.designSourceRefs],
    evidenceRefs: [...NEWSLETTER_L4_RELATION_TEMPLATE.evidenceRefs],
    relations,
  }
  return deepFreeze({
    ...body,
    contentHash: hashCanonicalContent(body),
  }) satisfies Readonly<SourceInteractionRelationSetV1>
}

export const CANONICAL_NEWSLETTER_L4_SOURCE_INTERACTION_RELATION_SET =
  instantiateCanonicalNewsletterSourceInteractionRelationSet(
    "l4-creator-journey",
  )

/**
 * Durable Studio receipts may carry only relation authority known to this
 * producer build. A public self-hash is not sufficient authorization.
 */
export function isCanonicalSourceInteractionRelationSet(
  value: unknown,
): value is SourceInteractionRelationSetV1 {
  try {
    if (
      typeof value !== "object" ||
      value === null ||
      Array.isArray(value) ||
      typeof (value as { applicationInstanceId?: unknown })
        .applicationInstanceId !== "string"
    ) {
      return false
    }
    const expected = instantiateCanonicalNewsletterSourceInteractionRelationSet(
      (value as { applicationInstanceId: string }).applicationInstanceId,
    )
    return (
      hashCanonicalContent(value) === hashCanonicalContent(expected)
    )
  } catch {
    return false
  }
}

function deepFreeze<T>(value: T): T {
  if (value && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value)
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child)
    }
  }
  return value
}
